import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { fmtTime12 } from '../../utils/time';

const fmtDT = v => { try { return format(new Date(v), 'd MMM yy h:mm a'); } catch { return v || '–'; } };
const varianceCell = v => {
  if (v == null) return <span className="text-slate-400">–</span>;
  const n = parseFloat(v);
  return (
    <span className={clsx('font-semibold text-xs', n > 0.1 ? 'text-red-600' : n < -0.1 ? 'text-amber-600' : 'text-green-600')}>
      {n > 0 ? '+' : ''}{n.toFixed(2)}h
    </span>
  );
};

const COLS = [
  { key: 'shift_date',      label: 'Shift Date' },
  { key: 'staff_name',      label: 'Staff Name' },
  { key: 'client_names',    label: 'Client(s)' },
  { key: 'shift_type',      label: 'Shift Type' },
  { key: 'rostered_start',  label: 'Rostered Start', render: fmtTime12 },
  { key: 'rostered_end',    label: 'Rostered End',   render: fmtTime12 },
  { key: 'rostered_hours',  label: 'Rostered Hrs',  render: v => v ? `${parseFloat(v).toFixed(2)}h` : '–' },
  { key: 'clock_in_time',   label: 'Clock In',      render: fmtDT },
  { key: 'clock_out_time',  label: 'Clock Out',     render: fmtDT },
  { key: 'actual_hours',    label: 'Actual Hrs',    render: v => v ? `${parseFloat(v).toFixed(2)}h` : '–' },
  { key: 'hours_variance',  label: 'Variance',      render: varianceCell },
  { key: 'approved_at',     label: 'Approved At',   render: fmtDT },
];

export default function TimesheetApprovedRostered() {
  const [data, setData]         = useState([]);
  const [staff, setStaff]       = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]   = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [staffId, setStaffId]   = useState('');
  const [clientId, setClientId] = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : r.data?.clients || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (staffId)  p.set('staff_id', staffId);
    if (clientId) p.set('client_id', clientId);
    api.get(`/reports/timesheet-approved-rostered?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, staffId, clientId]);

  useEffect(() => { load(); }, [load]);

  const totals = data.reduce((acc, r) => ({
    rostered: acc.rostered + parseFloat(r.rostered_hours || 0),
    actual:   acc.actual   + parseFloat(r.actual_hours   || 0),
    variance: acc.variance + parseFloat(r.hours_variance  || 0),
  }), { rostered: 0, actual: 0, variance: 0 });

  const selStaff  = staff.find(s => s.id === staffId);
  const selClient = clients.find(c => c.id === clientId);
  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selStaff  ? [{ label: 'Staff',  value: `${selStaff.first_name} ${selStaff.last_name}`,   onRemove: () => setStaffId('') }]  : []),
    ...(selClient ? [{ label: 'Client', value: `${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
  ];

  return (
    <ReportLayout title="Timesheet Report (Approved with Rostered Time)" data={data} filename="timesheet_approved_rostered"
      columns={COLS} activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Staff</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select></div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }>
      {data.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-4">
          {[
            { label: 'Rostered Hours', value: `${totals.rostered.toFixed(2)}h` },
            { label: 'Actual Hours',   value: `${totals.actual.toFixed(2)}h` },
            { label: 'Total Variance', value: `${totals.variance > 0 ? '+' : ''}${totals.variance.toFixed(2)}h`, color: Math.abs(totals.variance) > 1 ? 'text-red-600' : 'text-green-600' },
          ].map(card => (
            <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">{card.label}</div>
              <div className={clsx('text-2xl font-bold', card.color || 'text-slate-900')}>{card.value}</div>
            </div>
          ))}
        </div>
      )}
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination} />
    </ReportLayout>
  );
}
